<template>
  <v-sheet class="mx-auto" max-width="2000">
    <v-card class="mx-auto" elevation="0"
            title="">
      <v-row align="center" no-gutters>
        <v-col cols="12" lg="5" xl="5" xxl="5">
          <v-card class="mx-auto" elevation="0"
                  title="Most common ATC codes in the Prescribed Drug Register"
                  ref="most-common-atc-in-lmed">
            <v-card-subtitle class="text-wrap">Aggregated statistics for the most common ATC codes reported in the
              Prescribed Drug Register between 1 Jul 2005 - 31 Dec 2024, counting AFTER index date. Full ATC code
              specified if possible.
            </v-card-subtitle>
            <v-row no-gutters>
              <v-col>
                <v-data-table :headers="mostCommonAtcInLmedHeaders"
                              :items="mostCommonAtcInLmedItems"
                              density="compact"
                              :fixed-header="true"
                              :items-per-page="0"
                              class="striped-table">
                  <template #bottom></template>
                  <template v-slot:no-data>
                    <v-progress-circular v-if="isLoadingMostCommonAtcInLmed" color="primary" indeterminate></v-progress-circular>
                    <span v-else>No prescribed drug data available for this dataset</span>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="text-right">
                <vue-to-print :content="() => this.$refs['most-common-atc-in-lmed'].$el"
                              :document-title="icd + '-' + 'most-common-atc-in-lmed'"
                              remove-after-print>
                  <template v-slot:trigger>
                    <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                  </template>
                </vue-to-print>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col cols="0" lg="1" xl="1" xxl="1"/>
        <v-col cols="12" lg="5" xl="5" xxl="5">
          <v-card class="mx-auto" elevation="0"
                  title="Most common ATC codes in the Patient Register (in/outpatient)"
                  ref="most-common-atc-in-par">
            <v-card-subtitle class="text-wrap">Aggregated statistics for the most common ATC codes reported in the
              Patient Register (in/outpatient) between 1 Jan 2000 - 31 Dec 2024, counting AFTER index date. Full ATC
              code specified if possible.
            </v-card-subtitle>
            <v-row no-gutters>
              <v-col>
                <v-data-table :headers="mostCommonAtcInParHeaders"
                              :items="mostCommonAtcInParItems"
                              density="compact"
                              :fixed-header="true"
                              :items-per-page="0"
                              class="striped-table">
                  <template #bottom></template>
                  <template v-slot:no-data>
                    <v-progress-circular v-if="isLoadingMostCommonAtcInPar" color="primary" indeterminate></v-progress-circular>
                    <span v-else>No prescribed drug data available for this dataset</span>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="text-right">
                <vue-to-print :content="() => this.$refs['most-common-atc-in-par'].$el"
                              :document-title="icd + '-' + 'most-common-atc-in-par'"
                              remove-after-print>
                  <template v-slot:trigger>
                    <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                  </template>
                </vue-to-print>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
      <v-row align="center" no-gutters>
        <v-col cols="12" lg="5" xl="5" xxl="5">
          <v-card class="mx-auto" elevation="0"
                  title="Most common ATC codes in all registers"
                  ref="most-common-atc-total">
            <v-card-subtitle class="text-wrap">Aggregated statistics for the most common ATC codes reported across all
              included data sources, counting AFTER index date. Full ATC code specified if possible.
            </v-card-subtitle>
            <v-row no-gutters>
              <v-col>
                <v-data-table :headers="mostCommonAtcTotalHeaders"
                              :items="mostCommonAtcTotalItems"
                              density="compact"
                              :fixed-header="true"
                              :items-per-page="0"
                              class="striped-table">
                  <template #bottom></template>
                  <template v-slot:no-data>
                    <v-progress-circular v-if="isLoadingMostCommonAtcTotal" color="primary" indeterminate></v-progress-circular>
                    <span v-else>No treatment data available for this dataset</span>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="text-right">
                <vue-to-print :content="() => this.$refs['most-common-atc-total'].$el"
                              :document-title="icd + '-' + 'most-common-atc-total'"
                              remove-after-print>
                  <template v-slot:trigger>
                    <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                  </template>
                </vue-to-print>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
        <v-col cols="0" lg="1" xl="1" xxl="1"/>
        <v-col cols="12" lg="5" xl="5" xxl="5">
          <v-card class="mx-auto" elevation="0"
                  :title="mostCommonKvaTitle"
                  ref="most-common-kva">
            <v-card-subtitle class="text-wrap">{{ mostCommonKvaSubTitle }}</v-card-subtitle>
            <v-row no-gutters>
              <v-col>
                <v-data-table :headers="mostCommonKvaHeaders"
                              :items="mostCommonKvaItems"
                              density="compact"
                              :fixed-header="true"
                              :items-per-page="0"
                              class="striped-table">
                  <template #bottom></template>
                  <template v-slot:no-data>
                    <v-progress-circular v-if="isLoadingMostCommonKva" color="primary" indeterminate></v-progress-circular>
                    <span v-else>No intervention data available for this dataset</span>
                  </template>
                </v-data-table>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col class="text-right">
                <vue-to-print :content="() => this.$refs['most-common-kva'].$el"
                              :document-title="icd + '-' + 'most-common-kva'"
                              remove-after-print>
                  <template v-slot:trigger>
                    <v-btn variant="plain" icon="mdi-printer" class="end"></v-btn>
                  </template>
                </vue-to-print>
              </v-col>
            </v-row>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
  </v-sheet>
</template>

<script>
import {defineComponent} from 'vue';
import axios from "axios";
import { VueToPrint } from "vue-to-print";

export default defineComponent({
  name: 'NeurologicalDiseaseTreatmentPatternPrescribedDrugs',
  props: {
    icd: String,
    visible: Boolean
  },
  components: {
    VueToPrint
  },
  data: () => ({
    isLoadingMostCommonAtcInLmed: true,
    isLoadingMostCommonAtcInPar: true,
    isLoadingMostCommonAtcTotal: true,
    isLoadingMostCommonKva: true,
    mostCommonAtcInLmedHeaders: [
      {
        title: 'ATC',
        align: 'start',
        key: 'atc',
        sortable: false
      },
      {
        title: 'Name',
        align: 'start',
        key: 'atcDescription',
        sortable: false
      },
      {
        title: 'Number of patients receiving drug',
        align: 'start',
        key: 'numberOfPatients',
        sortable: false
      },
      {
        title: 'Percentage among total number of patients',
        align: 'start',
        key: 'percentage',
        sortable: false
      }
    ],
    mostCommonAtcInLmedItems: [],
    mostCommonAtcInParHeaders: [
      {
        title: 'ATC',
        align: 'start',
        key: 'atc',
        sortable: false
      },
      {
        title: 'Name',
        align: 'start',
        key: 'atcDescription',
        sortable: false
      },
      {
        title: 'Number of patients receiving drug',
        align: 'start',
        key: 'numberOfPatients',
        sortable: false
      },
      {
        title: 'Percentage among total number of patients',
        align: 'start',
        key: 'percentage',
        sortable: false
      }
    ],
    mostCommonAtcInParItems: [],
    mostCommonAtcTotalHeaders: [
      {
        title: 'ATC',
        align: 'start',
        key: 'atc',
        sortable: false
      },
      {
        title: 'Name',
        align: 'start',
        key: 'atcDescription',
        sortable: false
      },
      {
        title: 'Number of patients receiving drug',
        align: 'start',
        key: 'numberOfPatients',
        sortable: false
      },
      {
        title: 'Percentage among total number of patients',
        align: 'start',
        key: 'percentage',
        sortable: false
      }
    ],
    mostCommonAtcTotalItems: [],
    mostCommonKvaTitle: 'Most common KVÅ codes in the Patient Register',
    mostCommonKvaSubTitle: 'Aggregated statistics for the most common KVÅ codes reported in the Patient Register.',
    mostCommonKvaHeaders: [],
    mostCommonKvaItems: []
  }),
  methods: {
    async loadMostCommonAtcInLmedData() {
      return axios.get('neurological-disease/most-common-atc-in-lmed',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
            }
          });
    },
    async loadMostCommonAtcInParData() {
      return axios.get('neurological-disease/most-common-atc-in-par',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
            }
          });
    },
    async loadMostCommonAtcTotalData() {
      return axios.get('neurological-disease/most-common-atc-total',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
            }
          });
    },
    async loadMostCommonKvaData() {
      return axios.get('neurological-disease/most-common-kva',
          {
            headers: { Authorization: `Bearer ${this.$store.getters.stateUser.keycloak_token}` },
            params: {
              icd: this.icd,
            }
          });
    }
  },
  async mounted() {
    await Promise.all([
      this.loadMostCommonAtcInLmedData(),
      this.loadMostCommonAtcInParData(),
      this.loadMostCommonAtcTotalData(),
      this.loadMostCommonKvaData()
    ]).then(responses => {
      this.mostCommonAtcInLmedItems = responses[0].data
      this.mostCommonAtcInParItems = responses[1].data
      this.mostCommonAtcTotalItems = responses[2].data
      this.mostCommonKvaTitle = responses[3].data?.title ?? this.mostCommonKvaTitle
      this.mostCommonKvaSubTitle = responses[3].data?.subTitle ?? this.mostCommonKvaSubTitle
      this.mostCommonKvaHeaders = (responses[3].data?.headers ?? []).map(header => ({
        title: header.title,
        align: 'start',
        key: header.key,
        sortable: false
      }))
      this.mostCommonKvaItems = responses[3].data?.data ?? []
    }).finally(() => {
      this.isLoadingMostCommonAtcInLmed = false
      this.isLoadingMostCommonAtcInPar = false
      this.isLoadingMostCommonAtcTotal = false
      this.isLoadingMostCommonKva = false
    })
  }
});
</script>

<style scoped>
.striped-table :deep(tbody tr:nth-child(odd)) {
  background-color: rgba(0, 0, 0, .10);
}

@media print {
  .mx-auto {
    zoom: 50%;
  }
}
</style>
